/**
 * Reading and writing the billing wire format, with nothing rendered.
 *
 * Pure on purpose: every rule here is one a test can pin without mounting anything, and the list,
 * the detail screen and the settings screen all have to agree about all of them.
 */

import { dateTimeFormatter } from '../../../utils/dateTimeParts';
import type {
  BillingCollectionMethod,
  BillingInterval,
  BillingInvoice,
  BillingInvoiceDisplayStatus,
  BillingPaymentMethod,
  BillingPaymentMethodKind,
  BillingPlanStatus,
  BillingReminderKind,
  BillingReminderOutcome,
  BillingReminderSource,
  BillingStripeSetupState,
} from '../../../types/adminBilling';

/* -------------------------------------------------------------------------- money */

/**
 * Dollars as typed, to the integer cents the API stores.
 *
 * **Done with string arithmetic rather than `value * 100`.** Floating point makes `1234.56 * 100`
 * into `123455.99999999999` and `0.05 * 100` into `5.000000000000001`; rounding hides that today
 * and then loses a cent the day someone changes the rounding. Splitting the string at the decimal
 * point and adding two integers cannot be a cent out.
 *
 * Returns null for anything that is not a plain non-negative amount with at most two decimal
 * places, **including a third decimal**: `"1.234"` is refused rather than silently rounded, because
 * an invoice quietly becoming a cent different from the number an admin typed is worse than being
 * asked to type it again. `$`, spaces and thousands commas are accepted and stripped.
 */
export const dollarsToCents = (input: string): number | null => {
  const cleaned = input.trim().replace(/[$\s,]/g, '');
  if (!cleaned) return null;

  const match = /^(\d*)(?:\.(\d{0,2}))?$/.exec(cleaned);
  if (!match) return null;

  const whole = match[1] ?? '';
  const fraction = match[2];
  // "." on its own has neither half and is not a number.
  if (!whole && (fraction === undefined || fraction === '')) return null;

  const wholeCents = whole ? Number(whole) * 100 : 0;
  if (!Number.isSafeInteger(wholeCents)) return null;

  const fractionCents = fraction ? Number(fraction.padEnd(2, '0')) : 0;
  return wholeCents + fractionCents;
};

/**
 * Integer cents back to what belongs in a dollars text field: `123456` to `"1234.56"`.
 *
 * Always two decimal places, and no separators, currency symbol or locale grouping — this is the
 * value an input round-trips, not a value a person reads. `formatMoney` is the one for reading.
 */
export const centsToDollarsInput = (cents: number): string => {
  const safe = Number.isFinite(cents) ? Math.trunc(cents) : 0;
  const sign = safe < 0 ? '-' : '';
  const abs = Math.abs(safe);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
};

const numberFormatterCache = new Map<string, Intl.NumberFormat>();

const currencyFormatter = (currency: string): Intl.NumberFormat | null => {
  const cached = numberFormatterCache.get(currency);
  if (cached) return cached;
  try {
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency });
    numberFormatterCache.set(currency, formatter);
    return formatter;
  } catch {
    // `Intl` throws on a currency code it does not recognise, and the code comes from the server.
    return null;
  }
};

/**
 * Cents as a person reads them: `123456` to `"$1,234.56"`.
 *
 * A null amount is a dash rather than `$0.00` — "no plan" and "a plan for nothing" are different
 * facts and the list has to tell them apart. An unrecognised currency code falls back to the plain
 * number with the code beside it rather than throwing inside a table row.
 */
export const formatMoney = (
  cents: number | null | undefined,
  currency: string | null | undefined = 'USD'
): string => {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return '-';
  const code = currency && currency.trim() ? currency.trim().toUpperCase() : 'USD';
  const formatter = currencyFormatter(code);
  if (!formatter) return `${centsToDollarsInput(cents)} ${code}`;
  return formatter.format(cents / 100);
};

/* --------------------------------------------------------------------------- dates */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * A `"yyyy-MM-dd"` date, formatted **without ever constructing a `Date`**.
 *
 * `new Date("2026-09-01")` is parsed as UTC midnight and then printed in the reader's own zone, so
 * an admin anywhere west of UTC sees an invoice issued on the 1st as the 31st. There is no option
 * to `toLocaleDateString` that fixes that; not parsing it at all is the fix.
 *
 * Anything that is not exactly a date-only string returns null, which includes a full ISO instant.
 * That is deliberate: a caller that hands an instant to this function has a bug, and a dash makes
 * it visible rather than printing a plausible day that may be the wrong one.
 */
export const formatDateOnly = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  return `${MONTHS[month - 1]} ${day}, ${year}`;
};

/**
 * How late something is, in words: `42` to `"42 days past due"`.
 *
 * The count comes from the server, which derives it against its own clock. Working it out here from
 * a due date would make the list and the detail screen disagree by a day for anyone whose browser
 * is in a different zone from the platform, which on an overdue balance is the kind of difference
 * somebody quotes back to a customer.
 */
export const daysOverdueText = (days: number): string =>
  `${days} ${days === 1 ? 'day' : 'days'} past due`;

/** `formatDateOnly`, with a dash for anything missing, for use straight inside a cell. */
export const dateOnlyCell = (value: string | null | undefined): string =>
  formatDateOnly(value) ?? '-';

/**
 * An ISO-8601 instant in the reader's own zone: "Sep 23, 2026, 1:33 PM".
 *
 * Unlike a date-only field, an instant *should* move with the reader — "when did the reminder go
 * out" is a moment in time, not a day on a calendar.
 */
export const formatInstant = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return dateTimeFormatter('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(at);
};

export const instantCell = (iso: string | null | undefined): string => formatInstant(iso) ?? '-';

/* -------------------------------------------------------------------------- labels */

export const INTERVAL_LABEL: Record<BillingInterval, string> = {
  Monthly: 'Monthly',
  Annual: 'Annual',
};

export const PLAN_STATUS_LABEL: Record<BillingPlanStatus, string> = {
  Active: 'Active',
  Paused: 'Paused',
  Cancelled: 'Cancelled',
};

export const PAYMENT_METHOD_KIND_LABEL: Record<BillingPaymentMethodKind, string> = {
  None: 'No saved method',
  Card: 'Card',
  Ach: 'Bank transfer (ACH)',
};

/**
 * How an invoice was settled.
 *
 * `Stripe` is the mirror's honest "we do not know which", and it is listed here so a mirrored paid
 * invoice reads as something rather than as a blank. It is not among the choices an admin is
 * offered — see `ADMIN_PAID_METHODS` — because the API refuses it from a person.
 */
export const PAID_METHOD_LABEL: Record<BillingPaymentMethod, string> = {
  Manual: 'Manual (cheque or transfer)',
  Card: 'Card',
  Ach: 'Bank transfer (ACH)',
  Stripe: 'Collected by Stripe',
};

/** The methods somebody recording a payment by hand may pick. */
export const ADMIN_PAID_METHODS: readonly BillingPaymentMethod[] = ['Manual', 'Card', 'Ach'];

/** Sentence case, because these read inside a cell rather than as a shouted badge. */
export const DISPLAY_STATUS_LABEL: Record<BillingInvoiceDisplayStatus, string> = {
  open: 'Open',
  draft: 'Draft',
  overdue: 'Overdue',
  paid: 'Paid',
  void: 'Void',
  uncollectible: 'Uncollectible',
};

/**
 * A total Record, so adding a kind to the union makes the compiler ask for its wording here rather
 * than letting it reach a screen as a blank cell.
 *
 * The two Stripe kinds say Stripe, because the operator reading this ledger is deciding whether the
 * customer has already been chased and by whom. "Overdue" against a Stripe dunning email would
 * claim this system sent it.
 */
export const REMINDER_KIND_LABEL: Record<BillingReminderKind, string> = {
  BeforeDue: 'Before due',
  DueToday: 'Due today',
  Overdue: 'Overdue',
  StripeSent: 'Stripe sent the invoice',
  StripePaymentFailed: 'Stripe payment failed',
};

/**
 * Who sent a reminder.
 *
 * Named rather than left to the reader, because the same list carries both and "Sent" against a
 * Stripe dunning email reads as a claim that this system mailed the customer.
 */
export const REMINDER_SOURCE_LABEL: Record<BillingReminderSource, string> = {
  RosterPilot: 'Sent by RosterPilot',
  Stripe: 'Sent by Stripe',
};

export const REMINDER_OUTCOME_LABEL: Record<BillingReminderOutcome, string> = {
  sent: 'Sent',
  suppressed: 'Not sent',
  failed: 'Failed',
  pending: 'Waiting to send',
};

/**
 * How far an attempt to attach a payment method has got.
 *
 * Deliberately says what is outstanding rather than naming the state. `PendingMicrodeposits` is
 * the one that reads as done and is not: there is a stored bank account with a label on it that
 * cannot be charged until somebody at the college confirms two deposit amounts.
 */
export const STRIPE_SETUP_STATE_LABEL: Record<BillingStripeSetupState, string> = {
  None: 'Nothing in progress',
  AwaitingCustomer: 'Waiting on the organization to finish',
  PendingMicrodeposits: 'Waiting on two deposit amounts to be confirmed',
  Failed: 'The last attempt failed',
};

/* ------------------------------------------------------------------- stripe mirror */

/**
 * Which system bills an organization, said in one short phrase.
 *
 * It is stated on the list and again at the top of the detail screen, because everything else on
 * that screen follows from it: whether invoices can be raised here, whether mark paid and void
 * work, and who chases the money.
 */
export const billingModeLabel = (isStripeManaged: boolean): string =>
  isStripeManaged ? 'Stripe billed' : 'RosterPilot billed';

/** The sentence under that phrase on the detail screen. */
export const billingModeDescription = (isStripeManaged: boolean): string =>
  isStripeManaged
    ? 'Stripe raises these invoices and chases them. RosterPilot mirrors them, and the reminder settings on this platform do not apply to this organization.'
    : 'RosterPilot raises these invoices. Reminders go out from here, if they are switched on in billing settings.';

/**
 * Stripe's own status word, said plainly and attributed to Stripe.
 *
 * The word is passed through when this build has never heard of it, so a status Stripe adds later
 * still reads as something rather than disappearing.
 */
const STRIPE_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft at Stripe',
  open: 'Open at Stripe',
  paid: 'Paid at Stripe',
  uncollectible: 'Uncollectible at Stripe',
  void: 'Void at Stripe',
};

export const stripeStatusLabel = (status: string | null | undefined): string | null => {
  if (!status) return null;
  const key = status.trim().toLowerCase();
  if (!key) return null;
  return STRIPE_STATUS_LABEL[key] ?? `${key} at Stripe`;
};

/**
 * What to put in the cell that usually holds an invoice number.
 *
 * **A mirrored draft has no number, and never gets an invented one** — Stripe does not assign one
 * until it finalizes the invoice, and a made-up reference is one the customer cannot find. So the
 * cell falls back to Stripe's own status, and then to saying plainly that nothing has been issued.
 */
export const invoiceNumberCell = (
  invoice: Pick<BillingInvoice, 'invoiceNumber' | 'stripeStatus' | 'source'>
): string =>
  invoice.invoiceNumber ??
  stripeStatusLabel(invoice.stripeStatus) ??
  (invoice.source === 'Stripe' ? 'Not issued yet' : 'Invoice');

/**
 * What is still owed on an invoice Stripe has taken part of, or null when there is nothing extra
 * to say.
 *
 * Only shown when Stripe reports a remainder that is neither nothing nor the whole amount: equal to
 * the amount is an untouched invoice, which the amount column already says, and zero is a settled
 * one, which the status already says.
 */
export const amountRemainingText = (
  invoice: Pick<BillingInvoice, 'amountRemainingCents' | 'amountCents' | 'currency'>
): string | null => {
  const remaining = invoice.amountRemainingCents;
  if (typeof remaining !== 'number' || !Number.isFinite(remaining)) return null;
  if (remaining <= 0 || remaining >= invoice.amountCents) return null;
  return `${formatMoney(remaining, invoice.currency)} still owed`;
};

/* --------------------------------------------------------------- stripe subscription */

/**
 * How Stripe collects what the subscription bills, said as what actually happens.
 *
 * Named after the act rather than after Stripe's enum: "SendInvoice" tells an operator nothing
 * about whether a card is about to be charged, which is the only thing this choice decides.
 */
export const COLLECTION_METHOD_LABEL: Record<BillingCollectionMethod, string> = {
  SendInvoice: 'Stripe emails the invoice',
  ChargeAutomatically: 'Stripe charges the saved payment method',
};

/** The two choices in the order they are offered: the one that needs nothing on file first. */
export const COLLECTION_METHOD_CHOICES: readonly BillingCollectionMethod[] = [
  'SendInvoice',
  'ChargeAutomatically',
];

/** The label back to the value it names, for a picker that works in labels. */
export const collectionMethodForLabel = (label: string): BillingCollectionMethod | null =>
  COLLECTION_METHOD_CHOICES.find((method) => COLLECTION_METHOD_LABEL[method] === label) ?? null;

/**
 * **Stripe's own status word, printed verbatim**, so this screen and the Stripe dashboard say the
 * same thing about the same subscription. Nothing is translated into a local vocabulary and nothing
 * unrecognised is swallowed: a status Stripe adds later still appears.
 */
export const subscriptionStatusText = (status: string | null | undefined): string => {
  const word = status?.trim();
  return word ? word : 'No subscription';
};

/**
 * What that word means, where the word misleads on its own.
 *
 * **`trialing` is the one this exists for.** An agreed future start date is implemented at Stripe
 * as a trial that ends on the day, so `trialing` means "billing starts on the date you set", not
 * "this college is on a free trial". An operator reading the bare word takes a paying customer for
 * a trial account, and there is no way to tell the two apart from the status alone.
 *
 * Returns null where Stripe's word says everything, rather than padding the screen with a gloss of
 * "active" that nobody needs.
 */
export const subscriptionStatusNote = (status: string | null | undefined): string | null => {
  switch (status?.trim().toLowerCase()) {
    case 'trialing':
      return 'Trialing is how Stripe reports a subscription that has not started billing yet. It means billing starts on the date that was set for it, not that this is a free trial.';
    case 'incomplete':
      return 'Stripe raised the first invoice and nothing has paid it yet. A subscription left incomplete expires at Stripe.';
    case 'incomplete_expired':
      return 'The first invoice was never paid, so Stripe expired the subscription. It bills nothing. Create a new one to start again.';
    case 'past_due':
      return 'The latest invoice is past its due date. Stripe carries on chasing it.';
    case 'unpaid':
      return 'Stripe gave up collecting the latest invoice. It raises no further invoices for this subscription.';
    case 'canceled':
      return 'Stripe raises no further invoices for this subscription. The invoices it already raised are unchanged.';
    default:
      return null;
  }
};

/**
 * What a subscription bills each period, as a person reads it: "$1,234.56 monthly".
 *
 * **Stripe's figure, not the plan's.** The plan records the deal that was agreed; this is what the
 * customer is actually charged, and the two can differ — which is exactly the case worth seeing.
 */
export const subscriptionAmountText = (
  amountCents: number | null,
  currency: string | null,
  interval: BillingInterval | null
): string => {
  if (amountCents === null) return '-';
  const money = formatMoney(amountCents, currency);
  return interval ? `${money} ${INTERVAL_LABEL[interval].toLowerCase()}` : money;
};

/**
 * The plan cell on the list: the amount and how often it repeats, or that there is no plan.
 *
 * "No plan" is a real answer here, not a missing value, so it is a sentence rather than a dash.
 */
export const planCellText = (
  amountCents: number | null,
  currency: string | null,
  interval: BillingInterval | null
): string => {
  if (amountCents === null || interval === null) return 'No plan';
  return `${formatMoney(amountCents, currency)} ${INTERVAL_LABEL[interval].toLowerCase()}`;
};

/* ----------------------------------------------------------------- reminder schedule */

export type DaysBeforeDueParse =
  | { ok: true; days: number[] }
  | { ok: false; message: string };

/**
 * The "days before due" field, which an admin types as a list: `7, 1`.
 *
 * Kept as text in the form and parsed on save rather than being a set of number inputs, because
 * the list has no fixed length. Duplicates are dropped and the result is sorted furthest-out
 * first, which is the order the reminders actually go in.
 */
export const parseDaysBeforeDue = (input: string): DaysBeforeDueParse => {
  const trimmed = input.trim();
  if (!trimmed) return { ok: true, days: [] };

  const parts = trimmed
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  const days: number[] = [];
  for (const part of parts) {
    if (!/^\d+$/.test(part)) {
      return { ok: false, message: `"${part}" is not a whole number of days.` };
    }
    const day = Number(part);
    // 90 is the API's MaxDaysBeforeDue. 365 passed here and came back a 400.
    if (day < 1 || day > 90) {
      return { ok: false, message: 'Days before due must be between 1 and 90.' };
    }
    if (!days.includes(day)) days.push(day);
  }

  return { ok: true, days: days.sort((a, b) => b - a) };
};

/** The stored list back into the text field: `[7, 1]` to `"7, 1"`. */
export const formatDaysBeforeDue = (days: number[]): string => days.join(', ');
